<script setup>
import Grid from "@/components/Grid.vue";
import useGrid from "@/composables/useGrid";
import {ref, reactive, watch, inject} from "vue";
import {constants} from "@/views/search/manual/searchConstants";
import PulseLoader from "vue-spinner/src/MoonLoader.vue";
import {useStore} from "vue2-helpers/vuex";
import searchService from "@/services/search.service";

// eslint-disable-next-line
import Teleport from "vue2-teleport";
import AiStartupsModal from "@/views/search/manual/AiStartupsModal.vue";
import SearchTopBarCounter from "@/views/search/manual/SearchTopBarCounter.vue";

const props = defineProps(['columns', 'sort', 'gridAction', 'show']);

const store = useStore();
const {items, listLoading, listLoaded, gridParams, gridMethods} = useGrid(getListMethod, 25, listUpdated);
const searchType = inject('searchType');
const oldSearch = inject('oldSearch');
const customView = inject('customView');
const elasticQuery = inject('elasticQuery');
const promptQuery = inject('promptQuery');
const searchParams = inject('searchParams');
const query = inject('query');
const queryText = inject('queryText');
const negativeQueryText = inject('negativeQueryText');
const filters = inject('filters');

const aiListLoading = ref(false);
const boostIds = [];
const aiStartups = ref(null);
const aiStartupsModal = ref(null);
const firstLoading = ref(true);

const addCellData = reactive({
    companyType: searchType,
    fromSearch: true,
});

const notShownCount = ref(0);

watch(() => props.columns, (value) => {
    gridMethods.setColumns(value);

    if (!firstLoading.value) {
        gridMethods.resetList();
    }
});

watch(() => props.sort, (value) => {
    gridMethods.changeSort(value);
});

function listUpdated(responseData) {
    if (!customView) {
        console.log(responseData.request);
        elasticQuery.value = JSON.stringify(responseData.request);

        if ('not_shown_count' in responseData) {
            notShownCount.value = parseInt(responseData.not_shown_count);
        } else {
            notShownCount.value = 0;
        }
    }
}

function getListMethod(pageParams) {
    let requestParams = {
        searchType,
        query: {
            main: query,
            prompt: promptQuery,
            boost_ids: boostIds.value,
        },

        params: searchParams,
    }

    return searchService.getList({requestParams, pageParams});
}

function updateList() {
    gridMethods.updateList();
}

function resetList() {
    firstLoading.value = false;
    boostIds.value = [];
    aiStartups.value = null;

    if (queryText.value && !store.getters.settings.disable_ai) {
        getAiResults();
    }

    gridMethods.resetList();
}

function changePage(page) {
    if (page === 1) {
        gridParams.currentPage = 1;
        resetList();
    } else {
        gridMethods.changePage(page);
    }
}

function getAiResults() {
    aiListLoading.value = true;

    const data = {
        columns: gridParams.columns.map(column => column.name),
        filters
    }

    const gptRequest = searchService.getAIStartups(queryText.value, negativeQueryText.value, 'gpt', data);
    const ppxRequest = searchService.getAIStartups(queryText.value, negativeQueryText.value, 'ppx', data);

    const sources = [
        'gpt',
        'ppx'
    ];

    Promise.all([gptRequest, ppxRequest])
        .then((responses) => {
            aiStartups.value = [];

            responses.forEach((response, index) => {
                response.data.forEach(startup => {
                    let startupIndex = aiStartups.value.findIndex(item => item.id === startup.id);

                    if (startupIndex < 0) {
                        startup.source = [sources[index]];

                        boostIds.value.push(startup.id);
                        aiStartups.value.push(startup);
                    } else {
                        aiStartups.value[startupIndex].source.push(sources[index]);
                    }
                });
            })

            if (boostIds.value.length) {
                gridMethods.updateList();
            }

            aiListLoading.value = false;
        })
}

defineExpose({
    items,
    gridParams,
    updateList,
    resetList,
})
</script>

<template>
    <div v-if="show">
        <PulseLoader :loading="listLoading || aiListLoading" :color="constants.spinnerColor"/>

        <template v-if="items.length">
            <Grid
                :columns="gridParams.columns"
                :has-checkbox-column="true"
                :add-cell-data="addCellData"
                :data="items"
                :page-length="gridParams.pageLength"
                :total-rows-count="gridParams.totalRowsCount"
                :sort="gridParams.sort"
                :currentPage="gridParams.currentPage"
                :select-no-limits="store.getters.isDaikinEnterprise"
                @changePage="changePage"
                @changeSort="gridMethods.changeSort"
                @selectedRowsChanged="gridMethods.changeSelectedRows"
                @selectAllResults="gridMethods.selectAllResults"
                @action="gridAction"
            >
                <template #counter>
                    <SearchTopBarCounter
                        v-if="!oldSearch"
                        :grid-params="gridParams"
                    />
                </template>
            </Grid>

            <div v-if="notShownCount" class="upgrade-membership">
                <h5 class="upgrade-membership__title">{{notShownCount}} more companies match this search</h5>
                <p>Upgrade your SwitchPitch membership to see them.</p>

                <a class="btn btn-primary btn-lg" href="mailto:info@switchpitch.com">Contact Sales</a>
            </div>
        </template>

        <div v-else-if="listLoaded" class="not-found">
            <div>
                <template v-if="searchType === constants.SEARCH_TYPE_STARTUPS">
                    <h2 class="heading2 heading2--startups">No Results</h2>

                    <template v-if="!customView && store.getters.isEnterprise && !store.getters.isFreeEnterprise">
                        <p>If you need help finding good startups, try messaging an ecosystem on the platform. They’d be happy to share their startups with you.</p>

                        <a :href="'/ecosystems'" class="btn btn-primary btn-lg">View Ecosystems</a>
                    </template>
                </template>

                <template v-if="searchType === constants.SEARCH_TYPE_ECOSYSTEMS">
                    <h2 class="heading2 heading2--ecosystems">No ecosystems</h2>
                    <p>Try removing or changing some of your keywords</p>
                </template>

                <template v-if="searchType === constants.SEARCH_TYPE_ENTERPRISES">
                    <h4 class="heading4">No enterprises</h4>
                    <p>Try removing or changing some of your keywords</p>
                </template>
            </div>
        </div>

        <Teleport v-if="store.state.user.company_id === 112572 && aiStartups !== null" to="#top-ai-startups" class="ml-auto">
            <a
                class="topbar__side__link"
                @click="aiStartupsModal.open()"
            >
                Top results ({{aiStartups.length}})
            </a>
        </Teleport>

        <AiStartupsModal
            ref="aiStartupsModal"
            :startups="aiStartups"
        />
    </div>
</template>

<style scoped>

</style>
